import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Search, RefreshCw, Download, Zap, TrendingUp, TrendingDown,
  X, Clock, Activity, AlertTriangle, Eye, GitBranch,
  BarChart2, Filter, CheckCircle, Shield, Target, Network,
  Globe, Minus, Radio, Brain,
} from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, Tooltip as ReTooltip } from 'recharts';
import apiService from '../services/apiService';
import { API_BASE_URL } from '../config';

/* ─── Types ─────────────────────────────────────────────────────────────── */
interface NarrativeCluster {
  id: string;
  theme: string;
  riskVelocity: number;
  volume: number;
  x: number;
  y: number;
  pressure: number;
  coordination: number;
  linked_signals: number;
  origin_country: string;
  type: 'Election' | 'Health' | 'Supply Chain' | 'Disaster' | 'Geopolitical';
  ai_context: string;
  trend: 'up' | 'down' | 'stable';
  agents: number;
  agent_confidence: number;
  provenance: string[];
  actors: string[];
  velocity_history: number[];
  connections: string[];
}

/* ─── Mock Data ─────────────────────────────────────────────────────────── */
const MOCK_CLUSTERS: NarrativeCluster[] = [
  {
    id: '1', theme: 'Vaccine Mandate Backlash', riskVelocity: 0.92, volume: 540,
    x: -50, y: 70, pressure: 88, coordination: 92, linked_signals: 14,
    origin_country: 'US', type: 'Health', trend: 'up', agents: 7, agent_confidence: 94,
    ai_context: '98% Vel1 driven by 14 coordinated VoIP + domain signals — shell company amplification detected',
    provenance: ['Twitter/X — 3,200 posts', 'Telegram — 840 channel relays', 'Facebook — 120 coordinated pages'],
    actors: ['Sanctioned Actor: Igor Petrov', 'Shell Domain: factcheck-news.tk', 'VoIP Cluster: +1(555)'],
    velocity_history: [45, 52, 61, 70, 82, 88, 92],
    connections: ['3', '5'],
  },
  {
    id: '2', theme: 'Supply Chain Collapse Rumor', riskVelocity: 0.42, volume: 210,
    x: 22, y: 30, pressure: 38, coordination: 31, linked_signals: 4,
    origin_country: 'UK', type: 'Supply Chain', trend: 'stable', agents: 5, agent_confidence: 79,
    ai_context: 'Low-coordination pattern — organic spread via financial forums, no state actor linkage detected',
    provenance: ['Reddit — 1,100 posts', 'LinkedIn — 300 shares', 'News aggregators — 45 syndications'],
    actors: ['Unknown financial blogger', 'Domain: marketcrash-news.co'],
    velocity_history: [38, 40, 39, 42, 41, 42, 42],
    connections: [],
  },
  {
    id: '3', theme: 'Election Integrity Fraud', riskVelocity: 0.87, volume: 890,
    x: -78, y: 91, pressure: 95, coordination: 89, linked_signals: 22,
    origin_country: 'RU', type: 'Election', trend: 'up', agents: 9, agent_confidence: 97,
    ai_context: 'Dominant EU + APAC acceleration — cross-linked with Igor Petrov infrastructure (OFAC SDN)',
    provenance: ['Telegram — 5,400 channel posts', 'Twitter/X — 12,000 retweets', 'YouTube — 340 repost clips'],
    actors: ['OFAC Sanctioned: Igor Petrov', 'Domain: election-truth.news [deleted]', 'IP: 192.168.4.152 (C2)'],
    velocity_history: [60, 65, 71, 78, 82, 86, 87],
    connections: ['1', '5'],
  },
  {
    id: '4', theme: 'Natural Disaster Relief Scam', riskVelocity: 0.61, volume: 340,
    x: 42, y: 52, pressure: 59, coordination: 47, linked_signals: 8,
    origin_country: 'ID', type: 'Disaster', trend: 'stable', agents: 6, agent_confidence: 84,
    ai_context: 'Coordinated donation fraud — 8 fake relief domains registered within 24h of earthquake event',
    provenance: ['Facebook — 2,100 shares', 'WhatsApp (estimated) — viral chain messages', 'Local news — 12 citations'],
    actors: ['Domain cluster: relieffund-*.com (8 domains)', 'Payment processor: unlicensed crypto wallet'],
    velocity_history: [50, 54, 58, 61, 60, 61, 61],
    connections: [],
  },
  {
    id: '5', theme: 'Boycott Amplification Wave', riskVelocity: 0.76, volume: 600,
    x: -12, y: 81, pressure: 74, coordination: 68, linked_signals: 11,
    origin_country: 'DE', type: 'Geopolitical', trend: 'up', agents: 7, agent_confidence: 88,
    ai_context: 'AI-generated protest imagery (SDXL fingerprint) amplified by 6 Telegram channels with shared admin',
    provenance: ['Twitter/X — 8,400 posts', 'Telegram — 1,200 channel relays', 'Instagram — 4,500 reposts'],
    actors: ['AI Image: activist_march [SDXL fingerprint]', 'Shared admin Telegram cluster (6 channels)', 'Domain: boycott-now.eu'],
    velocity_history: [55, 60, 65, 70, 73, 75, 76],
    connections: ['1', '3'],
  },
];

const TYPE_COLORS: Record<string, string> = {
  Election:       '#f43f5e',
  Health:         '#f97316',
  'Supply Chain': '#3b82f6',
  Disaster:       '#10b981',
  Geopolitical:   '#8b5cf6',
};

const TYPE_BG: Record<string, string> = {
  Election:       'bg-rose-500/15 text-rose-400 border-rose-500/30',
  Health:         'bg-orange-500/15 text-orange-400 border-orange-500/30',
  'Supply Chain': 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  Disaster:       'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  Geopolitical:   'bg-violet-500/15 text-violet-400 border-violet-500/30',
};

const NARRATIVE_TYPES = ['All Types', 'Election', 'Health', 'Supply Chain', 'Disaster', 'Geopolitical'];
const TIME_RANGES    = ['Last 24h', 'Last 7d', 'Last 30d'];
const SORT_OPTIONS   = ['Risk', 'Volume', 'Velocity', 'Recency'];
const RE_PHASES = [
  'Ingesting narrative fragments...',
  'Running multi-agent NLP clustering...',
  'Mapping entity co-occurrence graph...',
  'Scoring velocity acceleration...',
  'Linking Intelligence Center signals...',
  'Finalising threat narrative model...',
];

/* ─── SVG Map helpers ───────────────────────────────────────────────────── */
const SVG_W = 800; const SVG_H = 460;
const toSvgX = (x: number) => ((x + 100) / 200) * (SVG_W - 100) + 50;
const toSvgY = (y: number) => SVG_H - 50 - ((y / 100) * (SVG_H - 100));
const bubbleR = (vol: number) => Math.max(18, Math.min(58, Math.sqrt(vol) * 1.4));

/* ─── Mini sparkline ─────────────────────────────────────────────────────── */
const Spark: React.FC<{ data: number[]; color: string }> = ({ data, color }) => (
  <ResponsiveContainer width="100%" height={32}>
    <LineChart data={data.map((v, i) => ({ i, v }))}>
      <Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} dot={false} />
      <ReTooltip content={() => null} />
    </LineChart>
  </ResponsiveContainer>
);

/* ═══════════════════════════════════════════════════════════════════════════
   Main Component
═══════════════════════════════════════════════════════════════════════════ */
export const NarrativeIntelligence: React.FC = () => {
  const [clusters, setClusters] = useState<NarrativeCluster[]>(MOCK_CLUSTERS);
  const [hovered, setHovered]   = useState<NarrativeCluster | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [selected, setSelected] = useState<NarrativeCluster | null>(null);
  const [showActorLines, setShowActorLines]     = useState(false);
  const [showGeoOverlay, setShowGeoOverlay]     = useState(false);
  const [highlightHighRisk, setHighlightHighRisk] = useState(false);
  const [timeRange, setTimeRange] = useState('Last 7d');
  const [typeFilter, setTypeFilter] = useState('All Types');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy]           = useState('Risk');
  const [claimModal, setClaimModal]   = useState<NarrativeCluster | null>(null);
  const [reclusterRunning, setReclusterRunning] = useState(false);
  const [reclusterPct, setReclusterPct]         = useState(0);
  const [reclusterPhase, setReclusterPhase]     = useState('');
  const [reclusterDone, setReclusterDone]       = useState(false);
  const [countdown, setCountdown]               = useState('47:00');
  const [lastClustered, setLastClustered]       = useState('');
  const [cycleAcknowledged, setCycleAcknowledged] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);

  // BI Phase 2 integration: live industry narratives
  const [biNarratives, setBiNarratives] = useState<{ id: number; narrative_text: string; sentiment_score: number; volume_estimate: number; phase: string }[]>([]);
  const [biNarrativesLoading, setBiNarrativesLoading] = useState(false);
  const [biOverview, setBiOverview] = useState<{ overall_risk_score: number; dominant_narrative?: string; active_narratives?: number } | null>(null);

  useEffect(() => {
    const loadBI = async () => {
      setBiNarrativesLoading(true);
      try {
        const [narRes, ovRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/bi/market/Technology/narratives?limit=6`),
          fetch(`${API_BASE_URL}/api/bi/market/Technology/overview`),
        ]);
        if (narRes.ok) { const d = await narRes.json(); setBiNarratives(Array.isArray(d) ? d : []); }
        if (ovRes.ok)  { const d = await ovRes.json();  setBiOverview(d ?? null); }
      } catch { /* non-blocking */ } finally { setBiNarrativesLoading(false); }
    };
    loadBI();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const data = await (apiService.analytics as any).getCrisisClusters?.();
        if (Array.isArray(data) && data.length > 0) {
          setClusters(data.map((c: any, i: number) => ({
            ...MOCK_CLUSTERS[i % MOCK_CLUSTERS.length],
            id: String(c.id ?? i),
            theme: c.name || MOCK_CLUSTERS[i % MOCK_CLUSTERS.length].theme,
            riskVelocity: typeof c.risk_score === 'number' ? c.risk_score : MOCK_CLUSTERS[i % MOCK_CLUSTERS.length].riskVelocity,
            volume: c.value || MOCK_CLUSTERS[i % MOCK_CLUSTERS.length].volume,
            x: typeof c.sentiment === 'number' ? Math.round(c.sentiment) : MOCK_CLUSTERS[i % MOCK_CLUSTERS.length].x,
            y: Math.round((c.risk_score || 0) * 100),
          })));
        }
      } catch { /* keep mock */ }
    })();
  }, []);

  useEffect(() => {
    let total = 47 * 60;
    const iv = setInterval(() => {
      total--;
      if (total <= 0) { total = 47 * 60; }
      const m = Math.floor(total / 60).toString().padStart(2, '0');
      const s = (total % 60).toString().padStart(2, '0');
      setCountdown(`${m}:${s}`);
    }, 1000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const now = new Date();
    setLastClustered(now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) + ' IST');
  }, []);

  const handleRecluster = useCallback(() => {
    if (reclusterRunning) return;
    setReclusterRunning(true);
    setReclusterDone(false);
    setReclusterPct(0);
    setReclusterPhase(RE_PHASES[0]);
    let step = 0;
    const TOTAL = 80;
    const iv = setInterval(() => {
      step++;
      const pct = Math.min(Math.round((step / TOTAL) * 100), 100);
      setReclusterPct(pct);
      setReclusterPhase(RE_PHASES[Math.min(Math.floor(pct / 17), RE_PHASES.length - 1)]);
      if (step >= TOTAL) {
        clearInterval(iv);
        setReclusterRunning(false);
        setReclusterDone(true);
        setReclusterPhase('Re-clustering complete \u2014 5 narrative clusters updated \u00b7 2 new anomalies detected');
        const now = new Date();
        setLastClustered(now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) + ' IST');
      }
    }, 80);
  }, [reclusterRunning]);

  const handleArchiveExport = useCallback(() => {
    const payload = {
      exported_at: new Date().toISOString(),
      cycle: '7-Day Narrative Pulse Cycle',
      clusters: clusters.map(c => ({
        id: c.id, theme: c.theme, type: c.type, riskVelocity: c.riskVelocity,
        volume: c.volume, coordination: c.coordination, linked_signals: c.linked_signals,
        ai_context: c.ai_context, actors: c.actors, provenance: c.provenance,
      })),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `narrative_crisis_brief_${new Date().toISOString().slice(0, 10)}.json`;
    a.click(); URL.revokeObjectURL(url);
  }, [clusters]);

  const visibleClusters = clusters.filter(c => {
    const matchSearch = searchTerm === '' ||
      c.theme.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.origin_country.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.ai_context.toLowerCase().includes(searchTerm.toLowerCase());
    const matchType = typeFilter === 'All Types' || c.type === typeFilter;
    const matchRisk = !highlightHighRisk || c.riskVelocity >= 0.75;
    return matchSearch && matchType && matchRisk;
  });

  const sortedClaims = [...visibleClusters].sort((a, b) => {
    if (sortBy === 'Risk')     return b.riskVelocity - a.riskVelocity;
    if (sortBy === 'Volume')   return b.volume - a.volume;
    if (sortBy === 'Velocity') return b.coordination - a.coordination;
    return parseInt(b.id) - parseInt(a.id);
  });

  const overallRisk = Math.round(clusters.reduce((s, c) => s + c.pressure, 0) / clusters.length);

  const TrendIcon: React.FC<{ trend: string }> = ({ trend }) =>
    trend === 'up'   ? <TrendingUp   className="w-3.5 h-3.5 text-rose-400" /> :
    trend === 'down' ? <TrendingDown className="w-3.5 h-3.5 text-emerald-400" /> :
                       <Minus        className="w-3.5 h-3.5 text-slate-500" />;

  const handleSvgMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  return (
    <div className="w-full pb-12 bg-slate-950 min-h-screen text-slate-200">
      <div className="w-full px-0 pt-0">

        {/* PAGE HEADER */}
        <div className="flex items-start justify-between gap-6 mb-5">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-white mb-2 flex items-center gap-3">
              <Network className="w-9 h-9 text-violet-400" />
              Narrative Mapping
            </h1>
            <p className="text-slate-400">Multi-agent clustering of global narratives — pressure, intent, virality &amp; actor linkage</p>
          </div>
          <div className="flex items-center gap-2 bg-slate-900 border border-slate-800 rounded-xl px-4 py-2.5 w-80 focus-within:border-violet-500 transition-all">
            <Search className="w-4 h-4 text-slate-500 shrink-0" />
            <input
              className="bg-transparent flex-1 text-sm text-slate-200 placeholder-slate-600 outline-none"
              placeholder="Search narratives or linked entities..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="text-slate-600 hover:text-slate-300">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* KPI STRIP */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            {
              label: 'Active Clusters', value: clusters.length,
              icon: <Network className="w-5 h-5" />, color: 'text-violet-400', bg: 'bg-violet-500/10 border-violet-500/20',
            },
            {
              label: 'High-Risk Narratives', value: clusters.filter(c => c.riskVelocity >= 0.75).length,
              icon: <AlertTriangle className="w-5 h-5" />, color: 'text-rose-400', bg: 'bg-rose-500/10 border-rose-500/20',
              pulse: clusters.filter(c => c.riskVelocity >= 0.75).length > 0,
            },
            {
              label: 'Total Volume', value: clusters.reduce((s, c) => s + c.volume, 0).toLocaleString(),
              icon: <BarChart2 className="w-5 h-5" />, color: 'text-cyan-400', bg: 'bg-cyan-500/10 border-cyan-500/20',
            },
            {
              label: 'Platform Pressure', value: `${overallRisk}/100`,
              icon: <Shield className="w-5 h-5" />, color: overallRisk > 75 ? 'text-rose-400' : 'text-amber-400', bg: overallRisk > 75 ? 'bg-rose-500/10 border-rose-500/20' : 'bg-amber-500/10 border-amber-500/20',
            },
          ].map(k => (
            <div key={k.label} className={`rounded-xl border ${k.bg} p-4 flex items-center gap-3`}>
              <div className={`${k.color} relative`}>
                {k.icon}
                {(k as { pulse?: boolean }).pulse && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-rose-400 rounded-full animate-ping" />}
              </div>
              <div>
                <p className={`text-2xl font-black ${k.color}`}>{k.value}</p>
                <p className="text-xs text-slate-400 mt-0.5">{k.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* 7-DAY NARRATIVE PULSE BANNER */}
        {!cycleAcknowledged && (
          <div className="mb-6 bg-gradient-to-r from-violet-900/30 via-slate-900/60 to-rose-900/20 border border-violet-500/25 rounded-2xl p-5 shadow-xl">
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <span className="flex items-center gap-1.5 px-2.5 py-1 bg-violet-500/15 border border-violet-500/30 text-violet-300 text-xs font-bold rounded-lg">
                    <Radio className="w-3 h-3 animate-pulse" /> 7-DAY CYCLE COMPLETE
                  </span>
                  <span className="text-xs text-slate-500">Ended {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                </div>
                <p className="text-white font-bold text-lg leading-tight mb-1">
                  2,847 narrative fragments clustered
                  <span className="ml-2 text-sm font-semibold text-rose-400 bg-rose-500/10 px-2 py-0.5 rounded">+37% velocity spike</span>
                </p>
                <p className="text-slate-400 text-sm flex items-center gap-2 mb-3">
                  <Brain className="w-4 h-4 text-violet-400 shrink-0" />
                  <span>AI Insight: <span className="text-violet-300 font-semibold">Election Integrity Fraud cluster accelerating across EU + APAC vectors — linked to sanctioned actor infrastructure</span></span>
                </p>
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3 h-3 text-cyan-500" />
                    Next auto re-cluster in <span className="font-mono font-bold text-cyan-400 ml-1">{countdown}</span>
                  </span>
                  <span className="text-slate-700">·</span>
                  <span className="text-amber-400 font-semibold">4 claims showing anomalous velocity</span>
                </div>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <button
                  onClick={handleArchiveExport}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-violet-600 to-blue-600 hover:opacity-90 text-white rounded-xl text-sm font-bold transition-all shadow-lg shadow-violet-900/30"
                >
                  <Download className="w-4 h-4" /> Archive &amp; Export Crisis Brief
                </button>
                <button
                  onClick={handleRecluster}
                  disabled={reclusterRunning}
                  className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl text-sm font-bold transition-all ${reclusterRunning ? 'bg-rose-500/5 border-rose-500/20 text-rose-500/50 cursor-not-allowed' : 'bg-rose-500/15 hover:bg-rose-500/25 border-rose-500/40 text-rose-300'}`}
                >
                  <Zap className="w-4 h-4" /> {reclusterRunning ? 'Re-Clustering...' : 'Re-Cluster Now'}
                </button>
                <button
                  onClick={() => setCycleAcknowledged(true)}
                  className="text-xs text-slate-600 hover:text-slate-400 transition-colors text-center"
                >
                  Acknowledge &amp; dismiss
                </button>
              </div>
            </div>
            {reclusterRunning && (
              <div className="mt-4 pt-4 border-t border-violet-500/10">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs font-bold text-violet-400 uppercase tracking-widest flex items-center gap-1.5">
                    <Activity className="w-3 h-3 animate-pulse" /> Multi-Agent Re-Clustering
                  </p>
                  <span className="text-xs font-mono text-violet-300">{reclusterPct}%</span>
                </div>
                <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden mb-1.5">
                  <div className="h-full bg-gradient-to-r from-violet-500 to-rose-500 rounded-full transition-all duration-100" style={{ width: `${reclusterPct}%` }} />
                </div>
                <p className="text-xs text-slate-500">{reclusterPhase}</p>
              </div>
            )}
            {reclusterDone && !reclusterRunning && (
              <div className="mt-4 pt-4 border-t border-emerald-500/10 flex items-center gap-3">
                <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                <p className="text-xs text-emerald-300">{reclusterPhase}</p>
                <button className="ml-auto text-xs text-slate-600 hover:text-slate-400 transition-colors" onClick={() => setReclusterDone(false)}>dismiss</button>
              </div>
            )}
          </div>
        )}

        {cycleAcknowledged && (
          <div className="mb-4 flex items-center gap-3">
            <button onClick={() => setCycleAcknowledged(false)} className="text-xs text-violet-500 hover:text-violet-300 flex items-center gap-1.5 transition-colors">
              <Radio className="w-3 h-3" /> Show Narrative Pulse Banner
            </button>
            <span className="text-slate-700 text-xs">·</span>
            <span className="text-xs text-slate-600 flex items-center gap-1"><Clock className="w-3 h-3" /> Last re-clustered: <span className="font-mono text-slate-500 ml-1">{lastClustered}</span></span>
          </div>
        )}

        {/* MAIN GRID */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">

          {/* LEFT: Narrative Threat Map */}
          <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800 rounded-2xl shadow-2xl overflow-hidden">

            <div className="px-6 py-4 border-b border-slate-800">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-violet-400 animate-pulse" />
                  <h3 className="text-base font-bold text-white">Live Narrative Threat Map</h3>
                  <span className="text-xs text-slate-500 font-mono bg-slate-800 px-2 py-0.5 rounded">
                    {lastClustered ? `Re-clustered: ${lastClustered}` : 'Live'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={handleRecluster} disabled={reclusterRunning} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-500 hover:text-violet-400 transition-all disabled:opacity-40" title="Manual refresh">
                    <RefreshCw className={`w-4 h-4 ${reclusterRunning ? 'animate-spin' : ''}`} />
                  </button>
                  <button onClick={handleArchiveExport} className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-500 hover:text-violet-400 transition-all" title="Export">
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-0.5 bg-slate-800/60 rounded-lg p-0.5 border border-slate-700/50">
                  {TIME_RANGES.map(t => (
                    <button key={t} onClick={() => setTimeRange(t)} className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${timeRange === t ? 'bg-violet-600 text-white' : 'text-slate-500 hover:text-slate-300'}`}>{t}</button>
                  ))}
                </div>
                <div className="flex items-center gap-0.5 bg-slate-800/60 rounded-lg p-0.5 border border-slate-700/50 flex-wrap">
                  {NARRATIVE_TYPES.map(t => (
                    <button key={t} onClick={() => setTypeFilter(t)} className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${typeFilter === t ? 'bg-violet-600 text-white' : 'text-slate-500 hover:text-slate-300'}`}>{t}</button>
                  ))}
                </div>
                <button onClick={() => setHighlightHighRisk(v => !v)} className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold border transition-all ${highlightHighRisk ? 'bg-rose-500/15 border-rose-500/40 text-rose-300' : 'border-slate-700 text-slate-500 hover:border-slate-600 hover:text-slate-300'}`}>
                  <AlertTriangle className="w-3 h-3" /> High-Risk Only
                </button>
                <button onClick={() => setShowActorLines(v => !v)} className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold border transition-all ${showActorLines ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-300' : 'border-slate-700 text-slate-500 hover:border-slate-600 hover:text-slate-300'}`}>
                  <Network className="w-3 h-3" /> Actor Lines
                </button>
                <button onClick={() => setShowGeoOverlay(v => !v)} className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold border transition-all ${showGeoOverlay ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' : 'border-slate-700 text-slate-500 hover:border-slate-600 hover:text-slate-300'}`}>
                  <Globe className="w-3 h-3" /> Geo Overlay
                </button>
              </div>
            </div>

            {/* SVG Map */}
            <div className="relative" style={{ height: 460 }}>
              <svg ref={svgRef} viewBox={`0 0 ${SVG_W} ${SVG_H}`} className="w-full h-full relative z-10" onMouseMove={handleSvgMouseMove} onMouseLeave={() => setHovered(null)}>
                <defs>
                  <filter id="glow-v"><feGaussianBlur stdDeviation="4" result="blur" /><feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
                  <pattern id="dots" x="0" y="0" width="30" height="30" patternUnits="userSpaceOnUse"><circle cx="1" cy="1" r="0.8" fill="rgba(99,102,241,0.15)" /></pattern>
                </defs>
                <rect width={SVG_W} height={SVG_H} fill="url(#dots)" />
                <line x1={SVG_W / 2} y1={20} x2={SVG_W / 2} y2={SVG_H - 30} stroke="rgba(99,102,241,0.12)" strokeWidth="1" strokeDasharray="4 4" />
                <line x1={50} y1={SVG_H / 2} x2={SVG_W - 50} y2={SVG_H / 2} stroke="rgba(99,102,241,0.12)" strokeWidth="1" strokeDasharray="4 4" />
                <text x={SVG_W / 2 + 6} y={28} fill="rgba(148,163,184,0.45)" fontSize="10" fontFamily="monospace">Left-leaning | Right-leaning</text>

                {showActorLines && visibleClusters.map(c =>
                  c.connections.map(targetId => {
                    const target = visibleClusters.find(t => t.id === targetId);
                    if (!target || parseInt(c.id) >= parseInt(targetId)) return null;
                    return (
                      <line key={`${c.id}-${targetId}`} x1={toSvgX(c.x)} y1={toSvgY(c.y)} x2={toSvgX(target.x)} y2={toSvgY(target.y)} stroke="rgba(34,211,238,0.35)" strokeWidth="1.5" strokeDasharray="6 3">
                        <animate attributeName="stroke-dashoffset" from="0" to="-18" dur="1.5s" repeatCount="indefinite" />
                      </line>
                    );
                  })
                )}

                {visibleClusters.map(c => {
                  const cx = toSvgX(c.x); const cy = toSvgY(c.y); const r = bubbleR(c.volume);
                  const col = TYPE_COLORS[c.type] || '#8b5cf6';
                  const isHov = hovered?.id === c.id; const isSel = selected?.id === c.id;
                  const dimmed = highlightHighRisk && c.riskVelocity < 0.75;
                  const opacity = dimmed ? 0.2 : (isHov || isSel ? 1 : 0.82);
                  return (
                    <g key={c.id} style={{ cursor: 'pointer' }} onMouseEnter={() => setHovered(c)} onMouseLeave={() => setHovered(null)} onClick={() => setSelected(selected?.id === c.id ? null : c)}>
                      {c.riskVelocity >= 0.75 && (
                        <circle cx={cx} cy={cy} r={r + 8} fill="none" stroke={col} strokeWidth="1" opacity="0.3">
                          <animate attributeName="r" values={`${r + 4};${r + 16};${r + 4}`} dur="2.5s" repeatCount="indefinite" />
                          <animate attributeName="opacity" values="0.4;0;0.4" dur="2.5s" repeatCount="indefinite" />
                        </circle>
                      )}
                      {isSel && <circle cx={cx} cy={cy} r={r + 12} fill="none" stroke={col} strokeWidth="2" opacity="0.7" />}
                      <circle cx={cx} cy={cy} r={r} fill={col} fillOpacity={opacity * 0.28} stroke={col} strokeWidth={isHov || isSel ? 2.5 : 1.5} strokeOpacity={opacity} filter={isHov ? 'url(#glow-v)' : undefined} style={{ transition: 'stroke-width 0.15s' }} />
                      <circle cx={cx} cy={cy} r={r * c.riskVelocity} fill={col} fillOpacity={opacity * 0.55} />
                      <text x={cx} y={cy - 4} textAnchor="middle" fill="white" fontSize={Math.max(9, Math.min(12, r / 3.5))} fontWeight="700" fontFamily="system-ui" opacity={opacity} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                        {c.theme.split(' ').slice(0, 2).join(' ')}
                      </text>
                      <text x={cx} y={cy + 11} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="9" fontFamily="monospace" opacity={opacity} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                        {Math.round(c.riskVelocity * 100)}% vel
                      </text>
                      {showGeoOverlay && (
                        <text x={cx} y={cy + r + 16} textAnchor="middle" fill={col} fontSize="9" fontFamily="monospace" opacity={opacity * 0.9} style={{ pointerEvents: 'none' }}>{c.origin_country}</text>
                      )}
                    </g>
                  );
                })}

                <g transform={`translate(${SVG_W - 130}, ${SVG_H - 90})`}>
                  <rect x={0} y={0} width={120} height={80} rx={8} fill="rgba(15,23,42,0.8)" stroke="rgba(99,102,241,0.2)" strokeWidth="1" />
                  <text x={8} y={16} fill="rgba(148,163,184,0.8)" fontSize="9" fontFamily="monospace" fontWeight="700">BUBBLE SCALE</text>
                  {[{ vol: 200, label: 'Vol 200' }, { vol: 600, label: 'Vol 600' }].map((s, i) => (
                    <g key={i} transform={`translate(${18 + i * 56}, 48)`}>
                      <circle cx={0} cy={0} r={bubbleR(s.vol) * 0.55} fill="rgba(139,92,246,0.3)" stroke="rgba(139,92,246,0.6)" strokeWidth="1" />
                      <text x={0} y={bubbleR(s.vol) * 0.55 + 12} textAnchor="middle" fill="rgba(148,163,184,0.7)" fontSize="8" fontFamily="monospace">{s.label}</text>
                    </g>
                  ))}
                  <text x={8} y={72} fill="rgba(148,163,184,0.6)" fontSize="8" fontFamily="monospace">Inner fill = velocity %</text>
                </g>
              </svg>

              {hovered && (
                <div className="absolute z-50 pointer-events-none" style={{ left: Math.min(tooltipPos.x + 16, SVG_W - 240), top: Math.max(tooltipPos.y - 10, 8), maxWidth: 260 }}>
                  <div className="bg-slate-900/98 backdrop-blur-sm border border-slate-700/70 rounded-xl p-4 shadow-2xl">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <p className="font-bold text-white text-sm leading-tight">{hovered.theme}</p>
                      <span className={`text-xs px-2 py-0.5 rounded border font-bold shrink-0 ${TYPE_BG[hovered.type]}`}>{hovered.type}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono mb-3">
                      <span className="text-slate-500">Volume</span>       <span className="text-white font-bold">{hovered.volume.toLocaleString()}</span>
                      <span className="text-slate-500">Risk Velocity</span><span className="text-rose-400 font-bold">{Math.round(hovered.riskVelocity * 100)}%</span>
                      <span className="text-slate-500">Coordination</span> <span className="text-amber-400 font-bold">{hovered.coordination}%</span>
                      <span className="text-slate-500">Pressure</span>     <span className="text-orange-400 font-bold">{hovered.pressure}/100</span>
                      <span className="text-slate-500">OSINT Signals</span><span className="text-cyan-400 font-bold">{hovered.linked_signals} linked</span>
                      <span className="text-slate-500">Origin</span>       <span className="text-slate-300">{hovered.origin_country}</span>
                    </div>
                    <div className="mb-2">
                      <Spark data={hovered.velocity_history} color={TYPE_COLORS[hovered.type] || '#8b5cf6'} />
                      <p className="text-xs text-slate-600 text-center">7-day velocity curve</p>
                    </div>
                    <p className="text-xs text-slate-500 italic leading-snug">{hovered.ai_context.slice(0, 80)}...</p>
                    <p className="text-xs text-violet-400 mt-2 font-semibold">Click to drill into cluster</p>
                  </div>
                </div>
              )}

              <div className="absolute bottom-4 left-4 z-20 w-72">
                <div className="bg-slate-900/90 backdrop-blur-sm border border-violet-500/20 rounded-xl p-3.5 shadow-xl">
                  <p className="text-xs font-bold text-violet-400 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                    <Brain className="w-3 h-3" /> AI Narrative Threat Summary
                  </p>
                  <div className="space-y-2">
                    {clusters.filter(c => c.riskVelocity >= 0.75).slice(0, 3).map(c => (
                      <div key={c.id} className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: TYPE_COLORS[c.type] }} />
                        <p className="text-xs text-slate-400 leading-snug">
                          <span className="text-slate-200 font-semibold">{c.theme.split(' ').slice(0, 3).join(' ')}</span>
                          {' '}shows <span className="text-rose-400 font-bold">{c.coordination}% coordination</span> signal
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Cluster drill-down (inline) */}
            {selected && (
              <div className="border-t border-slate-800 px-6 py-5 bg-slate-900/30">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-xs text-violet-400 uppercase tracking-widest font-bold mb-1">Cluster Deep-Dive</p>
                    <h4 className="text-xl font-bold text-white">{selected.theme}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded border font-bold ${TYPE_BG[selected.type]}`}>{selected.type}</span>
                      <span className="text-xs text-slate-500">{selected.agents} agents clustered · {selected.agent_confidence}% consensus</span>
                    </div>
                  </div>
                  <button onClick={() => setSelected(null)} className="p-2 hover:bg-slate-800 rounded-lg text-slate-500 hover:text-white transition-all"><X className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-3">
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Threat Metrics</p>
                    {[
                      { label: 'Risk Velocity', val: `${Math.round(selected.riskVelocity * 100)}%`, bar: selected.riskVelocity, color: 'bg-rose-500' },
                      { label: 'Coordination',  val: `${selected.coordination}%`,                   bar: selected.coordination / 100, color: 'bg-amber-500' },
                      { label: 'Pressure',      val: `${selected.pressure}/100`,                    bar: selected.pressure / 100,     color: 'bg-orange-500' },
                    ].map(m => (
                      <div key={m.label}>
                        <div className="flex justify-between text-xs mb-1"><span className="text-slate-400">{m.label}</span><span className="text-white font-bold font-mono">{m.val}</span></div>
                        <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden"><div className={`h-full ${m.color} rounded-full`} style={{ width: `${m.bar * 100}%` }} /></div>
                      </div>
                    ))}
                    <div className="pt-2">
                      <p className="text-xs text-slate-500 mb-1">7-Day Velocity</p>
                      <Spark data={selected.velocity_history} color={TYPE_COLORS[selected.type] || '#8b5cf6'} />
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Provenance Chain</p>
                    <div className="space-y-2">
                      {selected.provenance.map((p, i) => <div key={i} className="flex items-center gap-2 text-xs"><span className="w-1.5 h-1.5 rounded-full bg-violet-400 shrink-0" /><span className="text-slate-300">{p}</span></div>)}
                    </div>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3 mt-4">Linked Actors</p>
                    <div className="space-y-2">
                      {selected.actors.map((a, i) => <div key={i} className="flex items-center gap-2 text-xs"><AlertTriangle className="w-3 h-3 text-rose-400 shrink-0" /><span className="text-rose-300">{a}</span></div>)}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">AI Context</p>
                    <p className="text-sm text-slate-300 leading-relaxed mb-4">{selected.ai_context}</p>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Quick Actions</p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { label: 'View Full Cluster', icon: <Eye className="w-3 h-3" /> },
                        { label: 'Counter-Narrative', icon: <GitBranch className="w-3 h-3" /> },
                        { label: 'Crisis Workspace',  icon: <Target className="w-3 h-3" /> },
                      ].map(a => (
                        <button key={a.label} className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-slate-800 border border-slate-700 hover:border-violet-500 hover:text-violet-300 text-slate-400 transition-all font-semibold">{a.icon} {a.label}</button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: Core Claims Sidebar */}
          <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-800">
              <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                <BarChart2 className="w-4 h-4 text-violet-400" /> Core Claims — Narrative Strike List
              </h3>
              <div className="bg-gradient-to-r from-rose-900/30 to-orange-900/20 border border-rose-500/20 rounded-xl px-4 py-3 mb-3">
                <p className="text-xs text-slate-500 uppercase tracking-widest font-bold mb-1">Platform-Wide Misinfo Pressure</p>
                <div className="flex items-end gap-2">
                  <span className="text-3xl font-black text-rose-400 font-mono">{overallRisk}</span>
                  <span className="text-slate-400 text-sm mb-1">/100</span>
                  <span className={`ml-auto text-xs font-bold px-2 py-1 rounded-lg ${overallRisk > 75 ? 'bg-rose-500/15 text-rose-400 border border-rose-500/30' : 'bg-amber-500/15 text-amber-400 border border-amber-500/30'}`}>
                    {overallRisk > 75 ? 'ELEVATED' : 'MODERATE'}
                  </span>
                </div>
                <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden mt-2">
                  <div className="h-full bg-gradient-to-r from-rose-500 to-orange-500 rounded-full" style={{ width: `${overallRisk}%` }} />
                </div>
                <p className="text-xs text-slate-500 mt-1">Elevated in {clusters.filter(c => c.riskVelocity > 0.75).length} clusters</p>
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                <span className="text-xs text-slate-600 mr-1">Sort:</span>
                {SORT_OPTIONS.map(s => (
                  <button key={s} onClick={() => setSortBy(s)} className={`text-xs px-2.5 py-1 rounded-lg border font-bold transition-all ${sortBy === s ? 'bg-violet-600/20 border-violet-500/50 text-violet-300' : 'border-slate-700 text-slate-600 hover:text-slate-400 hover:border-slate-600'}`}>{s}</button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: 620 }}>
              {sortedClaims.length === 0 && (
                <div className="text-center py-12 text-slate-600">
                  <Filter className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No narratives match current filters</p>
                </div>
              )}
              {sortedClaims.map(c => {
                const col = TYPE_COLORS[c.type] || '#8b5cf6';
                return (
                  <div key={c.id} className={`relative overflow-hidden rounded-xl border transition-all cursor-pointer group ${selected?.id === c.id ? 'bg-violet-900/20 border-violet-500/40' : 'bg-slate-800/30 border-slate-700/30 hover:bg-slate-800/50 hover:border-slate-700/60'}`} onClick={() => setClaimModal(c)}>
                    <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl" style={{ backgroundColor: col }} />
                    <div className="px-4 py-3 pl-5">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <TrendIcon trend={c.trend} />
                          <h4 className="font-bold text-slate-100 text-sm truncate group-hover:text-violet-300 transition-colors">{c.theme}</h4>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded border font-bold shrink-0 ${c.riskVelocity > 0.75 ? 'bg-rose-500/15 text-rose-400 border-rose-500/30' : 'bg-amber-500/15 text-amber-400 border-amber-500/30'}`}>
                          {Math.round(c.riskVelocity * 100)}% vel
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 leading-snug mb-2 line-clamp-2">{c.ai_context}</p>
                      <div className="flex items-center gap-3 text-xs font-mono mb-2">
                        <span className="text-slate-500">Vol <span className="text-slate-300 font-bold">{c.volume.toLocaleString()}</span></span>
                        <span className="text-slate-700">·</span>
                        <span className="text-slate-500">Coord <span className="text-amber-400 font-bold">{c.coordination}%</span></span>
                        <span className="text-slate-700">·</span>
                        <span className="text-slate-500">OSINT <span className="text-cyan-400 font-bold">{c.linked_signals}</span></span>
                      </div>
                      <div className="mb-2 -mx-1"><Spark data={c.velocity_history} color={col} /></div>
                      <div className="flex items-center gap-1.5 mb-2">
                        <Shield className="w-3 h-3 text-slate-600" />
                        <span className="text-xs text-slate-600">Clustered by <span className="text-slate-400 font-semibold">{c.agents} agents</span> — <span className="text-emerald-400 font-bold">{c.agent_confidence}% consensus</span></span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-1" onClick={e => e.stopPropagation()}>
                        <button onClick={() => setSelected(c)} className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-slate-700/50 border border-slate-600/50 hover:border-violet-500/60 hover:text-violet-300 text-slate-400 transition-all font-semibold">
                          <Network className="w-2.5 h-2.5" /> Link Map
                        </button>
                        <button className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-slate-700/50 border border-slate-600/50 hover:border-cyan-500/60 hover:text-cyan-300 text-slate-400 transition-all font-semibold">
                          <Eye className="w-2.5 h-2.5" /> View Cluster
                        </button>
                        <button className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-slate-700/50 border border-slate-600/50 hover:border-emerald-500/60 hover:text-emerald-300 text-slate-400 transition-all font-semibold">
                          <GitBranch className="w-2.5 h-2.5" /> Counter Brief
                        </button>
                        <button className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-slate-700/50 border border-slate-600/50 hover:border-rose-500/60 hover:text-rose-300 text-slate-400 transition-all font-semibold">
                          <Target className="w-2.5 h-2.5" /> Crisis WS
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="px-5 py-3 border-t border-slate-800 flex items-center gap-3">
              <span className="text-xs text-slate-600 flex items-center gap-1"><Clock className="w-3 h-3" /> {lastClustered || '—'}</span>
              <button onClick={handleRecluster} disabled={reclusterRunning} className="ml-auto flex items-center gap-1.5 text-xs text-violet-500 hover:text-violet-300 disabled:opacity-40 transition-colors font-semibold">
                <RefreshCw className={`w-3 h-3 ${reclusterRunning ? 'animate-spin' : ''}`} /> {reclusterRunning ? 'Re-clustering...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        {/* TYPE LEGEND */}
        <div className="mt-4 flex items-center gap-4 flex-wrap">
          <span className="text-xs text-slate-600">Narrative Type:</span>
          {Object.entries(TYPE_COLORS).map(([type, color]) => (
            <button
              key={type}
              onClick={() => setTypeFilter(typeFilter === type ? 'All Types' : type)}
              className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border transition-all font-semibold"
              style={{ borderColor: typeFilter === type ? color : 'rgba(71,85,105,0.4)', color: typeFilter === type ? color : 'rgba(100,116,139,1)', backgroundColor: typeFilter === type ? `${color}18` : 'transparent' }}
            >
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
              {type}
            </button>
          ))}
          <span className="ml-auto text-xs text-slate-600">{visibleClusters.length} of {clusters.length} clusters shown</span>
        </div>

        {/* BI INDUSTRY NARRATIVE INTELLIGENCE — Phase 2 Core Integration */}
        <div className="mt-6 bg-slate-900/50 backdrop-blur-xl border border-violet-500/20 rounded-2xl p-5 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Brain className="w-4 h-4 text-violet-400" />
              <span className="text-sm font-bold text-white">BI Industry Narrative Intelligence</span>
              <span className="text-xs text-slate-500">Technology sector · Phase 2 market signals</span>
            </div>
            {biNarrativesLoading && <RefreshCw className="w-3.5 h-3.5 text-violet-400 animate-spin" />}
            {biOverview && (
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500">Overall Risk</span>
                <span className={`text-sm font-black font-mono ${
                  (biOverview.overall_risk_score ?? 0) > 70 ? 'text-rose-400' :
                  (biOverview.overall_risk_score ?? 0) > 40 ? 'text-amber-400' : 'text-emerald-400'
                }`}>{biOverview.overall_risk_score?.toFixed(0) ?? '—'}/100</span>
              </div>
            )}
          </div>
          {biNarratives.length === 0 && !biNarrativesLoading ? (
            <p className="text-xs text-slate-600 text-center py-4">
              No industry narratives yet. Run a market scan in <span className="text-violet-400">Business Intelligence → Market Intelligence</span>.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {biNarratives.map(n => {
                const sent = n.sentiment_score ?? 0;
                const sentColor = sent < -0.2 ? 'text-rose-400' : sent > 0.2 ? 'text-emerald-400' : 'text-amber-400';
                const sentBg    = sent < -0.2 ? 'bg-rose-500/10 border-rose-500/20' : sent > 0.2 ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-amber-500/10 border-amber-500/20';
                return (
                  <div key={n.id} className={`rounded-xl border px-3 py-2.5 ${sentBg}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-bold uppercase ${sentColor}`}>
                        {sent < -0.2 ? 'Negative' : sent > 0.2 ? 'Positive' : 'Neutral'}
                      </span>
                      {n.volume_estimate != null && (
                        <span className="text-xs text-slate-500 ml-auto">{n.volume_estimate.toLocaleString()} est. vol.</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-200 leading-snug line-clamp-2">{n.narrative_text}</p>
                    {n.phase && <p className="text-xs text-slate-600 mt-1">{n.phase}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* CLAIM DETAIL MODAL */}
      {claimModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(2,6,23,0.85)', backdropFilter: 'blur(8px)' }} onClick={() => setClaimModal(null)}>
          <div className="bg-slate-900 border border-slate-700/60 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="px-8 py-6 border-b border-slate-800 flex items-start justify-between gap-4 sticky top-0 bg-slate-900 z-10">
              <div>
                <p className="text-xs text-violet-400 uppercase tracking-widest font-bold mb-1">Narrative Cluster Detail</p>
                <h2 className="text-2xl font-bold text-white">{claimModal.theme}</h2>
                <div className="flex items-center gap-3 mt-2">
                  <span className={`text-xs px-2 py-0.5 rounded border font-bold ${TYPE_BG[claimModal.type]}`}>{claimModal.type}</span>
                  <span className="text-xs text-slate-500">{claimModal.agents} agents · {claimModal.agent_confidence}% consensus</span>
                  <span className="text-xs text-slate-500">Origin: <span className="text-slate-300">{claimModal.origin_country}</span></span>
                </div>
              </div>
              <button onClick={() => setClaimModal(null)} className="p-2 rounded-xl hover:bg-slate-800 text-slate-500 hover:text-white transition-all shrink-0"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-8 py-6 grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Threat Metrics</p>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { label: 'Risk Velocity', val: `${Math.round(claimModal.riskVelocity * 100)}%`, color: 'text-rose-400' },
                      { label: 'Volume',         val: claimModal.volume.toLocaleString(),               color: 'text-violet-400' },
                      { label: 'Coordination',   val: `${claimModal.coordination}%`,                   color: 'text-amber-400' },
                      { label: 'Pressure',       val: `${claimModal.pressure}/100`,                    color: 'text-orange-400' },
                      { label: 'Linked Signals', val: String(claimModal.linked_signals),               color: 'text-cyan-400' },
                      { label: 'Trend',          val: claimModal.trend, color: claimModal.trend === 'up' ? 'text-rose-400' : claimModal.trend === 'down' ? 'text-emerald-400' : 'text-slate-400' },
                    ].map(m => (
                      <div key={m.label} className="bg-slate-800/50 rounded-xl p-3">
                        <p className="text-xs text-slate-500 mb-1">{m.label}</p>
                        <p className={`text-lg font-black font-mono ${m.color}`}>{m.val}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">7-Day Velocity Timeline</p>
                  <div className="bg-slate-800/30 rounded-xl p-3" style={{ height: 80 }}>
                    <Spark data={claimModal.velocity_history} color={TYPE_COLORS[claimModal.type] || '#8b5cf6'} />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">AI Intelligence Context</p>
                  <div className="bg-violet-900/10 border border-violet-500/15 rounded-xl p-4">
                    <p className="text-sm text-slate-300 leading-relaxed">{claimModal.ai_context}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Full Provenance Chain</p>
                  <div className="space-y-2">
                    {claimModal.provenance.map((p, i) => (
                      <div key={i} className="flex items-center gap-3 bg-slate-800/30 rounded-lg px-3 py-2">
                        <span className="w-5 h-5 rounded-full bg-violet-500/20 border border-violet-500/40 flex items-center justify-center text-xs font-bold text-violet-400">{i + 1}</span>
                        <span className="text-sm text-slate-300">{p}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Linked Intelligence Center Signals</p>
                  <div className="space-y-2">
                    {claimModal.actors.map((a, i) => (
                      <div key={i} className="flex items-center gap-3 bg-rose-900/10 border border-rose-500/15 rounded-lg px-3 py-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-rose-400 shrink-0" />
                        <span className="text-sm text-rose-300">{a}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Response Actions</p>
                  <div className="grid grid-cols-1 gap-2">
                    {[
                      { label: 'Link to Narrative Threat Map',     icon: <Network className="w-4 h-4" />, onClick: () => { setClaimModal(null); setSelected(claimModal); } },
                      { label: 'View Full Cluster Detail',          icon: <Eye className="w-4 h-4" /> },
                      { label: 'Generate Counter-Narrative Brief',  icon: <GitBranch className="w-4 h-4" /> },
                      { label: 'Add to Active Crisis Workspace',    icon: <Target className="w-4 h-4" /> },
                      { label: 'Export Narrative Attack Graph',     icon: <Download className="w-4 h-4" />, onClick: handleArchiveExport },
                    ].map(a => (
                      <button key={a.label} onClick={a.onClick} className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-violet-500/50 hover:bg-violet-900/15 hover:text-violet-300 text-slate-400 text-sm font-semibold transition-all text-left">
                        {a.icon} {a.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NarrativeIntelligence;
